package com.mgacreative.hotel.feature.channelmanager.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.lazy.rememberLazyListState
import com.mgacreative.hotel.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotel.feature.channelmanager.data.ChannelConnectionEntity
import com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun ChannelManagerScreen(
    onNavigateBack: () -> Unit = {},
    onNavigateToAllotment: () -> Unit = {}
) {
    val viewModel = koinViewModel<ChannelManagerViewModel>()
    val channels by viewModel.channels.collectAsState()
    val logs by viewModel.logs.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()
    val alert by viewModel.service.overbookingAlert.collectAsState()

    val listState = rememberLazyListState()
    var channelToEdit by remember { mutableStateOf<ChannelConnectionEntity?>(null) }
    var isNewChannelDialogOpen by remember { mutableStateOf(false) }

    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Column {
                        Text(
                            text = "Kanal Yöneticisi (Channel Manager)",
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFF0F172A)
                        )
                        Text(
                            text = "Tüm OTA kanalları ve acenta müsaitlik senkronizasyonu",
                            style = MaterialTheme.typography.bodySmall,
                            color = Color(0xFF64748B)
                        )
                    }
                },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Geri", tint = Color(0xFF0F172A))
                    }
                },
                actions = {
                    // Allotment & Senkronize Butonları
                    OutlinedButton(
                        onClick = onNavigateToAllotment,
                        shape = RoundedCornerShape(8.dp),
                        border = BorderStroke(1.dp, Color(0xFFCBD5E1)),
                        modifier = Modifier.padding(end = 8.dp)
                    ) {
                        Icon(Icons.Default.EventSeat, contentDescription = null, modifier = Modifier.size(16.dp), tint = Color(0xFF0F172A))
                        Spacer(Modifier.width(6.dp))
                        Text("Allotment", color = Color(0xFF0F172A), fontSize = 13.sp)
                    }

                    Button(
                        onClick = { viewModel.syncAllChannels() },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.padding(end = 12.dp)
                    ) {
                        Icon(Icons.Default.Sync, contentDescription = null, modifier = Modifier.size(16.dp), tint = Color.White)
                        Spacer(Modifier.width(6.dp))
                        Text("Tümünü Senkronize Et", color = Color.White, fontSize = 13.sp)
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(containerColor = Color.White)
            )
        },
        containerColor = Color(0xFFF8FAFC)
    ) { innerPadding ->
        LazyColumn(
            state = listState,
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
                .padding(horizontal = 16.dp)
                .verticalScrollbar(listState),
            contentPadding = PaddingValues(bottom = 32.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            item {
                Spacer(modifier = Modifier.height(4.dp))

                // Overbooking Uyarısı (Varsa)
                if (alert != null) {
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        colors = CardDefaults.cardColors(containerColor = Color(0xFFFEF2F2)),
                        border = BorderStroke(1.dp, Color(0xFFFECACA)),
                        shape = RoundedCornerShape(10.dp)
                    ) {
                        Row(
                            modifier = Modifier.padding(14.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            Icon(Icons.Default.Warning, contentDescription = null, tint = Color(0xFFDC2626))
                            Column(modifier = Modifier.weight(1f)) {
                                Text(
                                    text = "KRİTİK UYARI: ÇAKIŞAN REZERVASYON (OVERBOOKING)",
                                    fontWeight = FontWeight.Bold,
                                    color = Color(0xFF991B1B),
                                    fontSize = 13.sp
                                )
                                Text(text = alert ?: "", color = Color(0xFF7F1D1D), fontSize = 12.sp)
                            }
                            TextButton(onClick = { viewModel.service.clearAlert() }) {
                                Text("Kapat", color = Color(0xFF991B1B))
                            }
                        }
                    }
                    Spacer(Modifier.height(10.dp))
                }

                // Metrik Özet Çubuğu
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    val activeCount = channels.count { it.is_active }
                    val totalCount = channels.size

                    SummaryMetricCard(
                        title = "Aktif OTA Kanalları",
                        value = "$activeCount / $totalCount",
                        subtext = "Çift yönlü canlı bağlantı",
                        valueColor = Color(0xFF0F172A),
                        modifier = Modifier.weight(1f)
                    )

                    SummaryMetricCard(
                        title = "Ortalama Fiyat Marjı",
                        value = if (channels.isNotEmpty()) "%${((channels.map { it.markup_percentage }.average()) * 10).toInt() / 10.0}" else "%0.0",
                        subtext = "Baz fiyata eklenen komisyon",
                        valueColor = Color(0xFF2563EB),
                        modifier = Modifier.weight(1f)
                    )

                    SummaryMetricCard(
                        title = "Senkronizasyon Durumu",
                        value = if (isLoading) "Eşitleniyor..." else "Canlı (2-Way)",
                        subtext = "Stop-Sell koruması aktif",
                        valueColor = Color(0xFF16A34A),
                        modifier = Modifier.weight(1f)
                    )
                }
            }

            // Kanal Listesi Başlığı & "+ Yeni Kanal Bağla" Butonu
            item {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Default.Hub, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(20.dp))
                        Spacer(Modifier.width(8.dp))
                        Text(
                            text = "Bağlı Kanallar & Portallar (${channels.size})",
                            fontSize = 16.sp,
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFF0F172A)
                        )
                    }

                    Button(
                        onClick = { isNewChannelDialogOpen = true },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF0F172A)),
                        shape = RoundedCornerShape(8.dp),
                        contentPadding = PaddingValues(horizontal = 14.dp, vertical = 6.dp)
                    ) {
                        Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(16.dp), tint = Color.White)
                        Spacer(Modifier.width(6.dp))
                        Text("+ Yeni Kanal Bağla", fontSize = 13.sp, color = Color.White)
                    }
                }
            }

            // Kompakt Kare Kartlar Izgarası
            item {
                if (channels.isEmpty()) {
                    Card(
                        shape = RoundedCornerShape(12.dp),
                        colors = CardDefaults.cardColors(containerColor = Color.White),
                        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(40.dp),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Icon(Icons.Default.CloudOff, contentDescription = null, tint = Color(0xFF94A3B8), modifier = Modifier.size(48.dp))
                            Spacer(Modifier.height(12.dp))
                            Text(
                                text = "Henüz tanımlı bir OTA kanalı bulunmuyor.",
                                fontWeight = FontWeight.SemiBold,
                                color = Color(0xFF475569)
                            )
                            Spacer(Modifier.height(6.dp))
                            Text(
                                text = "Booking.com, Expedia, Airbnb veya dilediğiniz kanalı eklemek için yukarıdaki '+ Yeni Kanal Bağla' butonuna tıklayın.",
                                fontSize = 13.sp,
                                color = Color(0xFF94A3B8),
                                textAlign = TextAlign.Center
                            )
                        }
                    }
                } else {
                    // Kare Kompakt Kartlar Grid
                    FlowRow(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                        maxItemsInEachRow = Int.MAX_VALUE
                    ) {
                        channels.forEach { channel ->
                            ChannelSquareCard(
                                channel = channel,
                                onOpenSettings = { channelToEdit = channel },
                                onToggleStatus = { isActive -> viewModel.toggleChannel(channel, isActive) }
                            )
                        }
                    }
                }
            }

            // Canlı Senkronizasyon & Aktivite Logları
            if (logs.isNotEmpty()) {
                item {
                    Spacer(Modifier.height(10.dp))
                    Card(
                        shape = RoundedCornerShape(12.dp),
                        colors = CardDefaults.cardColors(containerColor = Color.White),
                        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Column(modifier = Modifier.padding(14.dp)) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Icon(Icons.Default.History, contentDescription = null, tint = Color(0xFF64748B), modifier = Modifier.size(18.dp))
                                    Spacer(Modifier.width(6.dp))
                                    Text("Son Senkronizasyon Logları", fontWeight = FontWeight.Bold, color = Color(0xFF0F172A), fontSize = 14.sp)
                                }
                                Text("Canlı Akış", fontSize = 12.sp, color = Color(0xFF16A34A), fontWeight = FontWeight.Medium)
                            }
                            Spacer(Modifier.height(8.dp))

                            logs.take(5).forEach { log ->
                                HorizontalDivider(color = Color(0xFFF1F5F9))
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(vertical = 8.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Column(modifier = Modifier.weight(1f)) {
                                        Text(text = log.channel_name, fontWeight = FontWeight.Bold, fontSize = 13.sp, color = Color(0xFF1E293B))
                                        Text(text = log.message, fontSize = 12.sp, color = Color(0xFF64748B))
                                    }
                                    Surface(
                                        shape = RoundedCornerShape(4.dp),
                                        color = if (log.status == "SUCCESS") Color(0xFFDCFCE7) else Color(0xFFFEE2E2)
                                    ) {
                                        Text(
                                            text = log.status,
                                            fontSize = 10.sp,
                                            fontWeight = FontWeight.Bold,
                                            color = if (log.status == "SUCCESS") Color(0xFF15803D) else Color(0xFFB91C1C),
                                            modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // Kanal Ekleme veya Düzenleme Modalı
    if (isNewChannelDialogOpen || channelToEdit != null) {
        ChannelConfigDialog(
            initialChannel = channelToEdit,
            onDismiss = {
                isNewChannelDialogOpen = false
                channelToEdit = null
            },
            onSave = { updatedChannel ->
                viewModel.saveChannel(updatedChannel)
                isNewChannelDialogOpen = false
                channelToEdit = null
            },
            onDelete = { channelId, channelName ->
                viewModel.deleteChannel(channelId, channelName)
                isNewChannelDialogOpen = false
                channelToEdit = null
            },
            onTestBooking = { channelName ->
                viewModel.testIncomingOtaBooking(channelName, "101", "2026-09-08")
            }
        )
    }
}

/**
 * Kompakt Kare Kanal Kartı (Grid)
 */
@Composable
private fun ChannelSquareCard(
    channel: ChannelConnectionEntity,
    onOpenSettings: () -> Unit,
    onToggleStatus: (Boolean) -> Unit
) {
    Card(
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        border = BorderStroke(1.dp, if (channel.is_active) Color(0xFFBFDBFE) else Color(0xFFE2E8F0)),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        modifier = Modifier
            .width(180.dp)
            .clickable { onOpenSettings() }
    ) {
        Column(
            modifier = Modifier.padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            // Üst Satır: İkon + Durum Rozeti
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .size(36.dp)
                        .clip(RoundedCornerShape(8.dp))
                        .background(if (channel.is_active) Color(0xFFEFF6FF) else Color(0xFFF1F5F9)),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        imageVector = Icons.Default.Language,
                        contentDescription = null,
                        tint = if (channel.is_active) Color(0xFF2563EB) else Color(0xFF94A3B8),
                        modifier = Modifier.size(20.dp)
                    )
                }

                Surface(
                    shape = RoundedCornerShape(4.dp),
                    color = if (channel.is_active) Color(0xFFDCFCE7) else Color(0xFFF1F5F9),
                    border = BorderStroke(1.dp, if (channel.is_active) Color(0xFFBBF7D0) else Color(0xFFE2E8F0))
                ) {
                    Text(
                        text = if (channel.is_active) "AKTİF" else "PASİF",
                        fontSize = 9.sp,
                        fontWeight = FontWeight.Bold,
                        color = if (channel.is_active) Color(0xFF15803D) else Color(0xFF64748B),
                        modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                    )
                }
            }

            // Kanal Adı ve Kodu
            Column {
                Text(
                    text = channel.channel_name,
                    fontSize = 15.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFF0F172A),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    text = "Kod: ${channel.channel_code}",
                    fontSize = 11.sp,
                    color = Color(0xFF94A3B8)
                )
            }

            // Komisyon / Marj
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color(0xFFF8FAFC), shape = RoundedCornerShape(6.dp))
                    .padding(horizontal = 8.dp, vertical = 4.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text("Marj:", fontSize = 11.sp, color = Color(0xFF64748B))
                Text(
                    text = "+%${channel.markup_percentage}",
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFF2563EB)
                )
            }

            // Alt Buton: Ayarlar & Bağlantı
            OutlinedButton(
                onClick = onOpenSettings,
                shape = RoundedCornerShape(6.dp),
                border = BorderStroke(1.dp, Color(0xFFCBD5E1)),
                modifier = Modifier
                    .fillMaxWidth()
                    .height(32.dp),
                contentPadding = PaddingValues(0.dp)
            ) {
                Icon(Icons.Default.Settings, contentDescription = null, modifier = Modifier.size(14.dp), tint = Color(0xFF475569))
                Spacer(Modifier.width(4.dp))
                Text("Ayarlar", fontSize = 11.sp, color = Color(0xFF475569), fontWeight = FontWeight.Medium)
            }
        }
    }
}

/**
 * Özet Metrik Kutucuğu
 */
@Composable
private fun SummaryMetricCard(
    title: String,
    value: String,
    subtext: String,
    valueColor: Color,
    modifier: Modifier = Modifier
) {
    Card(
        shape = RoundedCornerShape(10.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = modifier
    ) {
        Column(modifier = Modifier.padding(12.dp)) {
            Text(text = title, fontSize = 12.sp, color = Color(0xFF64748B), fontWeight = FontWeight.Medium)
            Spacer(Modifier.height(2.dp))
            Text(text = value, fontSize = 18.sp, fontWeight = FontWeight.Bold, color = valueColor)
            Spacer(Modifier.height(2.dp))
            Text(text = subtext, fontSize = 10.sp, color = Color(0xFF94A3B8))
        }
    }
}

/**
 * Kanal Bağlantı ve API Ayarları Modalı (CRUD + Connection Config)
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ChannelConfigDialog(
    initialChannel: ChannelConnectionEntity?,
    onDismiss: () -> Unit,
    onSave: (ChannelConnectionEntity) -> Unit,
    onDelete: (String, String) -> Unit,
    onTestBooking: (String) -> Unit
) {
    var channelName by remember { mutableStateOf(initialChannel?.channel_name ?: "") }
    var channelCode by remember { mutableStateOf(initialChannel?.channel_code ?: "") }
    var hotelIdExternal by remember { mutableStateOf(initialChannel?.hotel_id_external ?: "") }
    var apiKey by remember { mutableStateOf(initialChannel?.api_key ?: "") }
    var markupStr by remember { mutableStateOf(initialChannel?.markup_percentage?.toString() ?: "15.0") }
    var syncIntervalStr by remember { mutableStateOf(initialChannel?.sync_interval_minutes?.toString() ?: "15") }
    var isActive by remember { mutableStateOf(initialChannel?.is_active ?: true) }

    val presetChannels = listOf(
        "Booking.com" to "BOOKING",
        "Expedia" to "EXPEDIA",
        "Airbnb" to "AIRBNB",
        "Agoda" to "AGODA",
        "Trip.com" to "TRIP",
        "Otelz.com" to "OTELZ",
        "TatilSepeti" to "TATILSEPETI",
        "Hotelbeds" to "HOTELBEDS"
    )

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(16.dp),
        title = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = if (initialChannel == null) "Yeni Kanal Bağlantısı Ekle" else "Kanal Bağlantı Ayarları",
                    fontWeight = FontWeight.Bold,
                    fontSize = 17.sp,
                    color = Color(0xFF0F172A)
                )
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Default.Close, contentDescription = "Kapat", tint = Color(0xFF64748B))
                }
            }
        },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                // Hazır Şablon Seçimi (Yeni kanal ekleniyorsa)
                if (initialChannel == null) {
                    Text("Hazır Kanal Şablonu:", fontSize = 12.sp, color = Color(0xFF64748B), fontWeight = FontWeight.Medium)
                    FlowRow(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                        verticalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        presetChannels.forEach { (name, code) ->
                            Surface(
                                shape = RoundedCornerShape(6.dp),
                                color = if (channelName == name) Color(0xFF2563EB) else Color(0xFFF1F5F9),
                                modifier = Modifier.clickable {
                                    channelName = name
                                    channelCode = code
                                }
                            ) {
                                Text(
                                    text = name,
                                    fontSize = 11.sp,
                                    color = if (channelName == name) Color.White else Color(0xFF334155),
                                    modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                                )
                            }
                        }
                    }
                    Spacer(Modifier.height(4.dp))
                }

                // Kanal Adı ve Kodu
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    OutlinedTextField(
                        value = channelName,
                        onValueChange = { channelName = it },
                        label = { Text("Kanal Adı") },
                        placeholder = { Text("Örn: Booking.com") },
                        modifier = Modifier.weight(1.2f),
                        shape = RoundedCornerShape(8.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedContainerColor = Color.White,
                            unfocusedContainerColor = Color.White,
                            focusedBorderColor = Color(0xFF2563EB),
                            unfocusedBorderColor = Color(0xFFCBD5E1)
                        )
                    )

                    OutlinedTextField(
                        value = channelCode,
                        onValueChange = { channelCode = it.uppercase() },
                        label = { Text("Kanal Kodu") },
                        placeholder = { Text("BOOKING") },
                        modifier = Modifier.weight(0.8f),
                        shape = RoundedCornerShape(8.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedContainerColor = Color.White,
                            unfocusedContainerColor = Color.White,
                            focusedBorderColor = Color(0xFF2563EB),
                            unfocusedBorderColor = Color(0xFFCBD5E1)
                        )
                    )
                }

                // Otel / Tesis ID (Hotel ID / Property ID)
                OutlinedTextField(
                    value = hotelIdExternal,
                    onValueChange = { hotelIdExternal = it },
                    label = { Text("Tesis / Hotel ID (OTA Entegrasyon Kodu)") },
                    placeholder = { Text("Örn: 9482910") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(8.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedContainerColor = Color.White,
                        unfocusedContainerColor = Color.White,
                        focusedBorderColor = Color(0xFF2563EB),
                        unfocusedBorderColor = Color(0xFFCBD5E1)
                    )
                )

                // API Key / Token
                OutlinedTextField(
                    value = apiKey,
                    onValueChange = { apiKey = it },
                    label = { Text("API Anahtarı / XML Token") },
                    placeholder = { Text("OTA tarafından verilen API anahtarı") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(8.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedContainerColor = Color.White,
                        unfocusedContainerColor = Color.White,
                        focusedBorderColor = Color(0xFF2563EB),
                        unfocusedBorderColor = Color(0xFFCBD5E1)
                    )
                )

                // Fiyat Marjı & Senkronizasyon Periyodu
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    OutlinedTextField(
                        value = markupStr,
                        onValueChange = { markupStr = it },
                        label = { Text("Komisyon / Marj (%)") },
                        placeholder = { Text("15.0") },
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(8.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedContainerColor = Color.White,
                            unfocusedContainerColor = Color.White,
                            focusedBorderColor = Color(0xFF2563EB),
                            unfocusedBorderColor = Color(0xFFCBD5E1)
                        )
                    )

                    OutlinedTextField(
                        value = syncIntervalStr,
                        onValueChange = { syncIntervalStr = it },
                        label = { Text("Senk. Sıklığı (Dk)") },
                        placeholder = { Text("15") },
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(8.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedContainerColor = Color.White,
                            unfocusedContainerColor = Color.White,
                            focusedBorderColor = Color(0xFF2563EB),
                            unfocusedBorderColor = Color(0xFFCBD5E1)
                        )
                    )
                }

                // Aktif / Pasif Switch
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(Color(0xFFF8FAFC), shape = RoundedCornerShape(8.dp))
                        .padding(horizontal = 12.dp, vertical = 6.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column {
                        Text("Kanal Durumu", fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = Color(0xFF0F172A))
                        Text(if (isActive) "Satışa ve entegrasyona açık" else "Kanal kapatıldı (Stop-Sell)", fontSize = 11.sp, color = Color(0xFF64748B))
                    }
                    Switch(
                        checked = isActive,
                        onCheckedChange = { isActive = it },
                        colors = SwitchDefaults.colors(
                            checkedThumbColor = Color.White,
                            checkedTrackColor = Color(0xFF2563EB)
                        )
                    )
                }

                // Canlı Bağlantı / Test Butonu (Düzenleme modunda)
                if (initialChannel != null) {
                    Button(
                        onClick = { onTestBooking(channelName) },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFF1F5F9)),
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Icon(Icons.Default.Bolt, contentDescription = null, tint = Color(0xFF0F172A), modifier = Modifier.size(16.dp))
                        Spacer(Modifier.width(6.dp))
                        Text("Bağlantıyı Test Et (Örnek OTA Rezervasyonu Gönder)", color = Color(0xFF0F172A), fontSize = 12.sp)
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    if (channelName.isNotBlank() && channelCode.isNotBlank()) {
                        val entity = ChannelConnectionEntity(
                            id = initialChannel?.id,
                            channel_name = channelName.trim(),
                            channel_code = channelCode.trim(),
                            hotel_id_external = hotelIdExternal.trim().ifBlank { null },
                            api_key = apiKey.trim().ifBlank { null },
                            markup_percentage = markupStr.toDoubleOrNull() ?: 0.0,
                            sync_interval_minutes = syncIntervalStr.toIntOrNull() ?: 15,
                            is_active = isActive
                        )
                        onSave(entity)
                    }
                },
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                shape = RoundedCornerShape(8.dp)
            ) {
                Text("Ayarları Kaydet", color = Color.White, fontWeight = FontWeight.SemiBold)
            }
        },
        dismissButton = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                if (initialChannel != null && initialChannel.id != null) {
                    TextButton(
                        onClick = { onDelete(initialChannel.id, initialChannel.channel_name) },
                        colors = ButtonDefaults.textButtonColors(contentColor = Color(0xFFDC2626))
                    ) {
                        Text("Kanalı Sil", fontWeight = FontWeight.Medium)
                    }
                    Spacer(Modifier.width(4.dp))
                }
                TextButton(
                    onClick = onDismiss,
                    colors = ButtonDefaults.textButtonColors(contentColor = Color(0xFF64748B))
                ) {
                    Text("Vazgeç")
                }
            }
        }
    )
}
